import type { Platform } from '../platform/Platform';

export class MultiplatformHandler {
  public static setStartGameCallback(callback: () => void): void {
    const wrapper = window?.multiplatformWrapper;

    if (wrapper) {
      wrapper.setStartGameCallback(callback);
    } else {
      callback();
    }
  }

  public static gameStart(): void {
    window?.multiplatformWrapper?.gameStarted();
  }

  public static gameOver(levelIndex: number, progress: number): void {
    window?.multiplatformWrapper?.gameFinished(levelIndex, progress);
  }

  public static setGiftCallback(callback: (productID: string) => void): void {
    window?.multiplatformWrapper?.setGiftCallback((gift) => {
      try {
        const productID = gift.content.productID as unknown;
        if (typeof productID === 'string') {
          callback(productID);
        }
      } catch (e) {
        console.warn(e);
      }
    });
  }

  public getPlayerOverride(): MultiplatformConfig['playerOverride'] {
    return window?.multiplatformWrapper?.getConfig()?.playerOverride ?? null;
  }

  public getTrackingConfig(): MultiplatformConfig['tracking'] {
    const config = window?.multiplatformWrapper?.getConfig();
    return config?.tracking ?? null;
  }

  public getPlatformTrackingConfig(platform: Platform): MultiplatformConfig['tracking'] {
    // First try to get platform-specific tracking configuration
    const platformConfig = platform.getTrackingConfig();
    if (platformConfig) {
      return platformConfig;
    }

    // Fallback to multiplatform wrapper configuration
    return this.getTrackingConfig();
  }

  public getSentryConfig(): MultiplatformConfig['sentry'] {
    const config = window?.multiplatformWrapper?.getConfig();

    const fbConfig = __NO_SENTRY__
      ? null
      : {
          dns: __SENTRY_DSN__,
        };

    return config ? config.sentry : fbConfig;
  }

  public isShareEnabled(): boolean {
    return window?.multiplatformWrapper?.getConfig()?.share ?? true;
  }

  public getSplitTestConfig(): MultiplatformConfig['splitTest'] {
    const config = window?.multiplatformWrapper?.getConfig();

    return config
      ? config.splitTest
      : {
          gameSlug: 'hexa-stack',
        };
  }

  public areTournamentsEnabled(): boolean {
    return window?.multiplatformWrapper?.getConfig()?.tournaments ?? true;
  }

  public arePurchasesAvailable(): boolean {
    return window?.multiplatformWrapper?.getConfig()?.purchases ?? true;
  }

  public areSocialFeaturesAvailable(): boolean {
    return window?.multiplatformWrapper?.getConfig()?.social ?? true;
  }

  public async getUserId(): Promise<string | null> {
    return window?.multiplatformWrapper?.getUserID() ?? null;
  }

  public async getSupportEmail(): Promise<string | null> {
    return window?.multiplatformWrapper?.getSupportEmail() ?? null;
  }

  public areAdsAvailable(): boolean {
    return !window?.multiplatformWrapper?.getConfig()?.ads?.disabled;
  }

  public getFacebookGameId(): string | null {
    const config = window?.multiplatformWrapper?.getConfig();
    return config?.facebookGameId ?? null;
  }
}
